import React, { memo, useCallback, useEffect, useRef } from 'react';

import { useFocusEffect, useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';
import { InteractionManager } from 'react-native';

import { RemoveUnavailableItemsModal } from 'components/remove-unavailable-items-modal';
import { useShortCodeRefresh } from 'hooks/loyalty/use-short-code-refresh';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { useLoyaltyIncentivesAvailability } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import LoyaltyNotAvailableAtDeliveryMode from 'pages/loyalty/loyalty-not-available-at-delivery-mode';
import LoyaltyNotAvailableAtThisRestaurant from 'pages/loyalty/loyalty-not-available-at-this-restaurant';
import { LoyaltyNotEnrolled } from 'pages/loyalty/loyalty-not-enrolled';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import {
  ICartEntryType,
  ShortCodeState,
  useInRestaurantRedemptionContext,
} from 'state/loyalty/in-restaurant-redemption';
import { IOfferCartIncentive } from 'state/loyalty/in-restaurant-redemption/hooks/use-in-restaurant-redemption-cart/incentive-cart/types';
import { isServiceModeValid } from 'state/loyalty/in-restaurant-redemption/is-service-mode-valid';
import { useServiceModeLive } from 'state/service-mode';
import { ServiceMode } from 'state/service-mode/types';
import { routes } from 'utils/routing';

import { StyledLoyaltyLoader } from '../loyalty-offers/loyalty-offers.styled';

import LoyaltyRedemptionRedesign from './redesign/loyalty-redemption-redesign';

enum InRestaurantRedemptionStatus {
  unavailableAtDelivery = 'unavailableAtDelivery',
  unavailableAtRestaurant = 'unavailableAtRestaurant',
  notEnrolled = 'notEnrolled',
  noStoreSelected = 'noStoreSelected',
  redemptionDrawer = 'redemptionDrawer',
  redemptionBaseView = 'redemptionBaseView',
  empty = 'empty',
  unavailableOffers = 'unavailableOffers',
}

const LoyaltyInRestaurantRedemption = () => {
  const skipLoyaltyStoreValidation = useFlag(LaunchDarklyFlag.SKIP_LOYALTY_STORE_VALIDATION);
  const { noStoreSelected } = useStoreContext();
  const {
    openSignInModal,
    isAuthenticated,
    loading: authLoading,
    modalAuthIsOpen,
  } = useAuthContext();
  const { replace } = useRouter();
  const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();
  const isDesktop = useIsDesktopBreakpoint();

  const {
    inRestaurantRedemptionCart,
    inRestaurantLoyaltyEnabledAtRestaurantLoading,
    inRestaurantLoyaltyEnabledAtRestaurant,
    generateShortCode,
    shortCodeState,
    shortCodeLoading,
    inRestaurantRedemptionEnabled,
    resetInRestaurantRedemption,
  } = useInRestaurantRedemptionContext();

  useShortCodeRefresh();

  const serviceMode = useServiceModeLive();
  const isDelivery = serviceMode === ServiceMode.DELIVERY;

  const { loyaltyUserId } = useLoyaltyUserId();

  const redemptionNotEnabled =
    !inRestaurantRedemptionEnabled ||
    (!skipLoyaltyStoreValidation &&
      !inRestaurantLoyaltyEnabledAtRestaurantLoading &&
      !inRestaurantLoyaltyEnabledAtRestaurant);

  useEffect(() => {
    if (isDesktop) {
      replace(routes.base);
    }
  }, [isDesktop, replace]);

  const shouldAutoGenerateState =
    shortCodeState === ShortCodeState.None ||
    shortCodeState === ShortCodeState.Expired ||
    shortCodeState === ShortCodeState.RequestError;

  const shouldGenerate =
    loyaltyUserId && !redemptionNotEnabled && shouldAutoGenerateState && !shortCodeLoading;

  useEffect(() => {
    if (shouldGenerate) {
      generateShortCode();
    }
  }, [shouldGenerate, generateShortCode]);

  const unavailableOffers = inRestaurantRedemptionCart
    .filter(
      o => o.type === ICartEntryType.OFFER && !checkLoyaltyOfferAvailability(o.details?.offer)
    )
    // @ts-ignore - we already filter by offer
    .map(offer => offer?.details?.offer) as IOfferCartIncentive[];

  // TODO: Review this logic with the PMs
  // TODO: Extract to pure function and add unit tests
  const getStatus = () => {
    if (isDelivery && !isServiceModeValid({ serviceMode })) {
      return InRestaurantRedemptionStatus.unavailableAtDelivery;
    }

    if (!noStoreSelected && loyaltyUserId && redemptionNotEnabled) {
      return InRestaurantRedemptionStatus.unavailableAtRestaurant;
    }

    // Wait for auth to settle before treating the user as logged out. Deep links can
    // land here before session restore finishes and would otherwise open the sign-in drawer.
    // Ignore loading while the auth modal is already open — login/OTP mutations also flip
    // `loading`, and bouncing to empty would re-open SIGN_IN mid-flow.
    if (!isAuthenticated) {
      if (authLoading && !modalAuthIsOpen) {
        return InRestaurantRedemptionStatus.empty;
      }

      return InRestaurantRedemptionStatus.notEnrolled;
    }

    if (isAuthenticated && noStoreSelected) {
      return InRestaurantRedemptionStatus.noStoreSelected;
    }

    if (noStoreSelected && !skipLoyaltyStoreValidation) {
      return InRestaurantRedemptionStatus.noStoreSelected;
    }

    if (redemptionNotEnabled) {
      return InRestaurantRedemptionStatus.notEnrolled;
    }

    // TODO: consider adding a LoadingState
    if (inRestaurantLoyaltyEnabledAtRestaurantLoading || !loyaltyUserId) {
      return InRestaurantRedemptionStatus.empty;
    }

    if (unavailableOffers.length) {
      return InRestaurantRedemptionStatus.unavailableOffers;
    }

    return InRestaurantRedemptionStatus.redemptionBaseView;
  };

  const status = getStatus();
  const hasAutoOpenedSignInRef = useRef(false);

  // Reset the one-shot prompt when leaving My Code so a later visit can open again.
  useFocusEffect(
    useCallback(() => {
      return () => {
        hasAutoOpenedSignInRef.current = false;
      };
    }, [])
  );

  useFocusEffect(
    useCallback(() => {
      if (status !== InRestaurantRedemptionStatus.notEnrolled) {
        return;
      }

      // Dismissing the drawer leaves status at notEnrolled, so without a one-shot guard
      // this effect would reopen it in a loop.
      if (hasAutoOpenedSignInRef.current) {
        return;
      }

      // Wait for navigation interactions to finish before opening the drawer (iOS freeze).
      const task = InteractionManager.runAfterInteractions(() => {
        hasAutoOpenedSignInRef.current = true;
        openSignInModal();
      });

      return () => task.cancel();
    }, [openSignInModal, status])
  );

  // The rendered component should depend only on the status
  // In case we need to add more conditional logic we should do it inside getStatus method
  switch (status) {
    case InRestaurantRedemptionStatus.unavailableAtDelivery:
      return <LoyaltyNotAvailableAtDeliveryMode />;

    case InRestaurantRedemptionStatus.unavailableAtRestaurant:
      return <LoyaltyNotAvailableAtThisRestaurant />;

    case InRestaurantRedemptionStatus.notEnrolled:
      return <LoyaltyNotEnrolled />;

    case InRestaurantRedemptionStatus.redemptionBaseView:
      return <LoyaltyRedemptionRedesign />;

    case InRestaurantRedemptionStatus.unavailableOffers:
      return (
        <RemoveUnavailableItemsModal
          onConfirmCb={resetInRestaurantRedemption}
          unavailableOffersInRestaurantRedemption={unavailableOffers}
        />
      );

    case InRestaurantRedemptionStatus.empty:
    default:
      return <StyledLoyaltyLoader />;
  }
};

export default memo(LoyaltyInRestaurantRedemption);
